<template>
  <HeaderBar />

  <section class="carvia-container flex flex-col justify-center">
    <div class="mb-4 flex justify-end">
      <CVButton
        v-if="$can('create', 'LegalEntity')"
        :to="{ name: 'legalEntity', params: { id: 'new' } }"
      >
        {{ t('newLegalEntity') }}
      </CVButton>
    </div>

    <div v-if="isLoading" class="flex justify-center"><Spinner /></div>
    <p v-else-if="!legalEntities?.length" class="text-center text-sm text-obsidian-500">
      {{ t('noLegalEntitiesFound') }}
    </p>
    <div v-else class="flex flex-col gap-2">
      <router-link
        v-for="legalEntity in legalEntities"
        :key="legalEntity.id"
        :to="{ name: 'legalEntity', params: { id: legalEntity.id } }"
        class="rounded-md bg-white py-2 pl-3 pr-2 text-sm"
      >
        <div class="flex items-center gap-2">
          <div class="basis-[35%]">
            <p class="mb-1 font-medium">{{ t('name') }}</p>
            <p>{{ legalEntity.name }}</p>
          </div>
          <div class="basis-[35%]">
            <p class="mb-1 font-medium">{{ t('address') }}</p>
            <p>{{ `${legalEntity.street}, ${legalEntity.zip} ${legalEntity.city.de}` }}</p>
          </div>
          <div class="basis-[15%]">
            <p class="mb-1 font-medium">{{ t('invoiceNumberPrefix') }}</p>
            <p>{{ legalEntity.invoiceNumberPrefix || '–' }}</p>
          </div>
          <div class="basis-[15%]">
            <p class="mb-1 font-medium">{{ t('status') }}</p>
            <p>{{ legalEntity.isActive ? t('active') : t('inactive') }}</p>
          </div>
        </div>
      </router-link>
    </div>
  </section>
</template>

<script setup lang="ts">
import { watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import Spinner from '@/components/icons/Spinner.vue';
import { useUiStore } from '@/stores/ui.store';
import { useLegalEntities } from '@/queries/use-legal-entities';

const { t } = useI18n();
const uiStore = useUiStore();
watchEffect(() => {
  uiStore.setHeaderTitle(t('legalEntities'));
});

const { data: legalEntities, isLoading } = useLegalEntities();
</script>

<i18n lang="json">
{
  "en": {
    "legalEntities": "Legal entities",
    "newLegalEntity": "+ New legal entity",
    "noLegalEntitiesFound": "No legal entities found",
    "name": "Name",
    "address": "Address",
    "invoiceNumberPrefix": "Invoice prefix",
    "status": "Status",
    "active": "Active",
    "inactive": "Inactive"
  },
  "de": {
    "legalEntities": "Gesellschaften",
    "newLegalEntity": "+ Neue Gesellschaft",
    "noLegalEntitiesFound": "Keine Gesellschaften gefunden",
    "name": "Name",
    "address": "Adresse",
    "invoiceNumberPrefix": "Rechnungspräfix",
    "status": "Status",
    "active": "Aktiv",
    "inactive": "Inaktiv"
  },
  "es": {
    "legalEntities": "Sociedades",
    "newLegalEntity": "+ Nueva sociedad",
    "noLegalEntitiesFound": "No se encontraron sociedades",
    "name": "Nombre",
    "address": "Dirección",
    "invoiceNumberPrefix": "Prefijo de factura",
    "status": "Estado",
    "active": "Activa",
    "inactive": "Inactiva"
  }
}
</i18n>
